/**
 * ItemPicker — composer line typeahead.
 *
 * Pops a small modal next to the line's description input. Picking an item
 * hydrates **five** fields of the line in one shot — description, HSN, unit,
 * rate, tax rate — so the operator stops retyping the same SKU on every
 * invoice.
 *
 * Architecture choices:
 *   - **Additive, never required.** Free-form lines stay legal. The picker
 *     is a shortcut, not a gate.
 *   - **Item link + line snapshot.** Picking an item stores the `item_id`
 *     for stock movements while still snapshotting HSN/rate/tax on the line.
 *   - **Modal vs popover.** `@flint/ui` doesn't ship a Popover primitive;
 *     `DialogRoot` already handles focus trap, escape-to-close, and the
 *     accessible click-outside behaviour. Lightweight modal beats an
 *     ad-hoc absolute-positioned div with bespoke a11y wiring.
 *   - **Debounced query.** 200ms after typing stops fires
 *     `items.list({ usage: 'sale', q, limit: 20 })`.
 *
 * @see docs/scratch/invoices-audit-and-fix-plan.md PR-E.5 — context
 */
import { useEffect, useRef, useState } from "react";
import { Package, Search, X } from "lucide-react";
import { Card, CardBody, DialogRoot, DialogContent, DialogTitle } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";

export interface PickedItemValues {
  itemId: string;
  description: string;
  hsn: string;
  unit: string;
  ratePaise: bigint;
  taxRateBps: number;
}

export interface ItemPickerProps {
  /** Called once the operator picks an item — never with partial values. */
  onPick: (values: PickedItemValues) => void;
  /** Disabled when the composer is mid-issue. */
  disabled?: boolean | undefined;
}

export function ItemPicker({ onPick, disabled }: ItemPickerProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // 200ms debounce — typing fast doesn't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 200);
    return () => clearTimeout(t);
  }, [input]);

  // Pre-load on open so the operator sees recent items without typing.
  const list = trpc.items.list.useQuery(
    {
      limit: 20,
      tab: "all",
      usage: "sale",
      ...(debounced ? { q: debounced } : {}),
    },
    { enabled: open },
  );

  // Auto-focus the search input when the dialog opens — single-keystroke flow.
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  const items = list.data?.items ?? [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label="Pick item from catalogue"
        title="Pick from item catalogue"
        className="inline-flex items-center justify-center h-7 w-7 rounded text-muted hover:text-ink hover:bg-raised disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
      >
        <Package size={14} aria-hidden />
      </button>

      <DialogRoot open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 sm:max-w-[480px] max-h-[70vh] overflow-hidden flex flex-col">
          <DialogTitle className="sr-only">Pick item from catalogue</DialogTitle>

          {/* Search input */}
          <label className="flex items-center gap-2 px-3 py-3 border-b border-line text-sm">
            <Search size={14} className="text-muted shrink-0" aria-hidden />
            <input
              ref={inputRef}
              type="search"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Name or SKU…"
              className="flex-1 bg-transparent outline-none placeholder:text-muted"
            />
            {input ? (
              <button
                type="button"
                onClick={() => setInput("")}
                aria-label="Clear search"
                className="text-muted hover:text-ink"
              >
                <X size={12} aria-hidden />
              </button>
            ) : null}
          </label>

          {/* Results */}
          <div className="overflow-y-auto flex-1">
            {list.isLoading ? (
              <p className="p-4 text-sm text-muted">Loading…</p>
            ) : list.error ? (
              <p className="p-4 text-sm text-bad">{list.error.message}</p>
            ) : items.length === 0 ? (
              <Card>
                <CardBody className="text-center py-6 px-4">
                  <p className="text-sm font-medium">
                    {debounced ? "No items match." : "No sale items yet."}
                  </p>
                  <p className="text-xs text-muted mt-1">
                    {debounced
                      ? "Try a name prefix or paste the SKU."
                      : 'Tag items in /items with usage "sale" or "both" to use this picker.'}
                  </p>
                </CardBody>
              </Card>
            ) : (
              <ul role="listbox" aria-label="Item catalogue">
                {items.map((item) => {
                  const ratePaise = item.defaultRatePaise ? BigInt(item.defaultRatePaise) : 0n;
                  const taxBps = item.defaultTaxRateBps ?? 1800;
                  const ratePreview =
                    item.defaultRatePaise === null
                      ? "no default rate"
                      : `₹${(ratePaise / 100n).toLocaleString("en-IN")}/${item.unit}`;
                  const taxPreview = `${(taxBps / 100).toFixed(taxBps % 100 === 0 ? 0 : 2)}% GST`;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onPick({
                            itemId: item.id,
                            description: item.description ?? item.name,
                            hsn: item.hsn ?? "",
                            unit: item.unit,
                            ratePaise,
                            taxRateBps: taxBps,
                          });
                          setOpen(false);
                          setInput("");
                        }}
                        className="w-full text-left px-3 py-2.5 hover:bg-raised border-b border-line/50 last:border-b-0 focus:outline-none focus:bg-raised"
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-ink-2 truncate">
                            {item.name}
                          </span>
                          {item.sku ? (
                            <span className="font-mono text-xs text-muted shrink-0">
                              {item.sku}
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs text-muted mt-0.5">
                          {ratePreview} · {taxPreview}
                          {item.hsn ? <> · HSN {item.hsn}</> : null}
                          {item.usage === "both" ? (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-raised px-1.5 text-xs font-medium uppercase tracking-wide text-ink-2">
                              both
                            </span>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogContent>
      </DialogRoot>
    </>
  );
}
